import { ColumnDef } from "@tanstack/react-table";
import {
	enumToStringConverter,
	formatDate,
	moneyFormat,
} from "../../../helpers/appHelpers";

// Type definitions
interface Employee {
	firstName: string;
	lastName: string;
}

interface PayPeriod {
	code: string;
}

interface BonusGroup {
	name: string;
}

interface Currency {
	code: string;
}

interface CompanyCurrency {
	currency: Currency;
}

interface OvertimeGroupRow {
	id: number;
	name: string;
	code: string;
	description: string;
	minHoursRequired: number;
	maxHoursPermitted: number;
	taxable: boolean;
	active: boolean;
	createdAt?: string;
	modifiedAt?: string;
}

// ========== Overtime Groups Columns ==========
export const overtimeGroupColumns: ColumnDef<OvertimeGroupRow>[] = [
	{
		id: "name",
		accessorKey: "name",
		header: "Name",
		size: 220,
	},
	{
		id: "code",
		accessorKey: "code",
		header: "Code",
		size: 180,
	},
	{
		id: "description",
		accessorKey: "description",
		header: "Description",
		size: 400,
	},
	{
		id: "minHoursRequired",
		accessorKey: "minHoursRequired",
		header: "Min Hours Required",
		size: 180,
		cell: ({ row }) => Number(row.original.minHoursRequired).toFixed(2),
	},
	{
		id: "maxHoursPermitted",
		accessorKey: "maxHoursPermitted",
		header: "Max Hours Permitted",
		size: 200,
		cell: ({ row }) => Number(row.original.maxHoursPermitted).toFixed(2),
	},
	{
		id: "taxable",
		accessorKey: "taxable",
		header: "Taxable",
		size: 140,
		cell: ({ row }) => (row.original.taxable ? "Yes" : "No"),
	},
	{
		id: "active",
		accessorKey: "active",
		header: "Active",
		size: 140,
		cell: ({ row }) => (row.original.active ? "Yes" : "No"),
	},
];

export const overtimeGroupColumnVisibility: Record<string, boolean> = {
	name: true,
	code: true,
	description: true,
	minHoursRequired: true,
	maxHoursPermitted: true,
	taxable: false,
	active: true,
};

interface OvertimeRow {
	id: number;
	name: string;
	code: string;
	description: string;
	minHoursRequired: number;
	maxHoursPermitted: number;
	taxable: boolean;
	active: boolean;
	employeeBand?: {
		name: string;
	};
	group?: {
		name: string;
	};
	createdAt?: string;
	modifiedAt?: string;
}

// ========== Overtime Columns ==========
export const overtimeColumns: ColumnDef<OvertimeRow>[] = [
	{
		id: "name",
		accessorKey: "name",
		header: "Name",
		size: 220,
	},
	{
		id: "code",
		accessorKey: "code",
		header: "Code",
		size: 180,
	},
	{
		id: "description",
		accessorKey: "description",
		header: "Description",
		size: 400,
	},
	{
		id: "overtimeGroup",
		accessorKey: "group.name",
		header: "Overtime Group",
		size: 200,
		cell: ({ row }) => row.original.group?.name || "--",
	},
	{
		id: "employeeBand",
		accessorKey: "employeeBand.name",
		header: "Employee Band",
		size: 200,
		cell: ({ row }) => row.original.employeeBand?.name || "--",
	},
	{
		id: "minHoursRequired",
		accessorKey: "minHoursRequired",
		header: "Min Hours Required",
		size: 180,
		cell: ({ row }) => Number(row.original.minHoursRequired).toFixed(2),
	},
	{
		id: "maxHoursPermitted",
		accessorKey: "maxHoursPermitted",
		header: "Max Hours Permitted",
		size: 200,
		cell: ({ row }) => Number(row.original.maxHoursPermitted).toFixed(2),
	},
	{
		id: "taxable",
		accessorKey: "taxable",
		header: "Taxable",
		size: 140,
		cell: ({ row }) => (row.original.taxable ? "Yes" : "No"),
	},
	{
		id: "active",
		accessorKey: "active",
		header: "Active",
		size: 140,
		cell: ({ row }) => (row.original.active ? "Yes" : "No"),
	},
];

export const overtimeColumnVisibility: Record<string, boolean> = {
	name: true,
	code: true,
	description: true,
	overtimeGroup: true,
	employeeBand: true,
	minHoursRequired: true,
	maxHoursPermitted: true,
	taxable: false,
	active: true,
};

interface BonusGroupRow {
	id: number;
	name: string;
	code: string;
	description: string;
	active: boolean;
	minValue: number;
	maxValue: number;
	periodStartDate: string;
	periodEndDate: string;
	prorate: boolean;
	minEmploymentPeriod?: number;
	employmentPeriodUnit?: string;
	paymentFrequency?: string;
	paymentInstance?: number;
	companyCurrency?: CompanyCurrency;
	createdAt?: string;
	modifiedAt?: string;
}

// ========== Bonus Groups Columns ==========
export const bonusGroupColumns: ColumnDef<BonusGroupRow>[] = [
	{
		id: "name",
		accessorKey: "name",
		header: "Name",
		size: 220,
	},
	{
		id: "code",
		accessorKey: "code",
		header: "Code",
		size: 180,
	},
	{
		id: "description",
		accessorKey: "description",
		header: "Description",
		size: 400,
	},
	{
		id: "active",
		accessorKey: "active",
		header: "Active",
		size: 140,
		cell: ({ row }) => (row.original.active ? "Yes" : "No"),
	},
	{
		id: "minValue",
		accessorKey: "minValue",
		header: "Min Value",
		size: 180,
		cell: ({ row }) => {
			const amount = row.original.minValue;
			const currency = row.original.companyCurrency?.currency;
			return `${currency?.code || ""} ${moneyFormat(amount)}`;
		},
	},
	{
		id: "maxValue",
		accessorKey: "maxValue",
		header: "Max Value",
		size: 180,
		cell: ({ row }) => {
			const amount = row.original.maxValue;
			const currency = row.original.companyCurrency?.currency;
			return amount ? `${currency?.code || ""} ${moneyFormat(amount)}` : "--";
		},
	},
	{
		id: "periodStartDate",
		accessorKey: "periodStartDate",
		header: "Period Start Date",
		size: 200,
		cell: ({ row }) => formatDate(row.original.periodStartDate),
	},
	{
		id: "periodEndDate",
		accessorKey: "periodEndDate",
		header: "Period End Date",
		size: 200,
		cell: ({ row }) => formatDate(row.original.periodEndDate),
	},
	{
		id: "prorate",
		accessorKey: "prorate",
		header: "Prorate",
		size: 140,
		cell: ({ row }) => (row.original.prorate ? "Yes" : "No"),
	},
	{
		id: "minEmploymentPeriod",
		accessorKey: "minEmploymentPeriod",
		header: "Min Employment Period",
		size: 220,
		cell: ({ row }) => {
			const period = row.original.minEmploymentPeriod;
			const unit = row.original.employmentPeriodUnit;
			return period && unit
				? `${period} ${enumToStringConverter(unit)}(s)`
				: "--";
		},
	},
	{
		id: "paymentFrequency",
		accessorKey: "paymentFrequency",
		header: "Payment Frequency",
		size: 200,
		cell: ({ row }) =>
			row.original.paymentFrequency
				? enumToStringConverter(row.original.paymentFrequency)
				: "--",
	},
	{
		id: "paymentInstance",
		accessorKey: "paymentInstance",
		header: "Payment Instance",
		size: 180,
		cell: ({ row }) => row.original.paymentInstance || "--",
	},
];

export const bonusGroupColumnVisibility: Record<string, boolean> = {
	name: true,
	code: true,
	description: true,
	active: true,
	minValue: false,
	maxValue: false,
	periodStartDate: true,
	periodEndDate: true,
	prorate: true,
	minEmploymentPeriod: false,
	paymentFrequency: true,
	paymentInstance: false,
};

interface BonusRow {
	id: number;
	name: string;
	code: string;
	description: string;
	fixedComponent: number;
	rateComponent: number;
	active: boolean;
	minValue: number;
	maxValue?: number;
	periodStartDate: string;
	periodEndDate: string;
	prorate: boolean;
	minEmploymentPeriod?: number;
	employmentPeriodUnit?: string;
	paymentFrequency?: string;
	companyCurrency?: CompanyCurrency;
	group?: BonusGroup;
	employeeBonuses?: Array<{ employee: Employee }>;
	payPeriods?: PayPeriod[];
	createdAt?: string;
	modifiedAt?: string;
}

// ========== Bonus Columns ==========
export const bonusColumns: ColumnDef<BonusRow>[] = [
	{
		id: "name",
		accessorKey: "name",
		header: "Name",
		size: 220,
	},
	{
		id: "code",
		accessorKey: "code",
		header: "Code",
		size: 180,
	},
	{
		id: "description",
		accessorKey: "description",
		header: "Description",
		size: 400,
	},
	{
		id: "bonusGroup",
		accessorKey: "group.name",
		header: "Bonus Group",
		size: 200,
		cell: ({ row }) => row.original.group?.name || "--",
	},
	{
		id: "fixedComponent",
		accessorKey: "fixedComponent",
		header: "Fixed Component",
		size: 180,
		cell: ({ row }) => {
			const amount = row.original.fixedComponent;
			const currency = row.original.companyCurrency?.currency;
			return `${currency?.code || ""} ${moneyFormat(amount)}`;
		},
	},
	{
		id: "rateComponent",
		accessorKey: "rateComponent",
		header: "Rate Component",
		size: 180,
		cell: ({ row }) =>
			`${(Number(row.original.rateComponent) * 100).toFixed(2)}%`,
	},
	{
		id: "employees",
		accessorKey: "employeeBonuses",
		header: "Assigned Employees",
		size: 200,
		cell: ({ row }) => {
			const count = row.original.employeeBonuses?.length || 0;
			return count > 0 ? `${count} employee(s)` : "--";
		},
	},
	{
		id: "payPeriods",
		accessorKey: "payPeriods",
		header: "Pay Periods",
		size: 200,
		cell: ({ row }) => {
			const periods = row.original.payPeriods;
			return periods && periods.length > 0
				? periods.map((p) => p.code).join(", ")
				: "--";
		},
	},
	{
		id: "active",
		accessorKey: "active",
		header: "Active",
		size: 140,
		cell: ({ row }) => (row.original.active ? "Yes" : "No"),
	},
	{
		id: "minValue",
		accessorKey: "minValue",
		header: "Min Value",
		size: 180,
		cell: ({ row }) => {
			const amount = row.original.minValue;
			const currency = row.original.companyCurrency?.currency;
			return `${currency?.code || ""} ${moneyFormat(amount)}`;
		},
	},
	{
		id: "maxValue",
		accessorKey: "maxValue",
		header: "Max Value",
		size: 180,
		cell: ({ row }) => {
			const amount = row.original.maxValue;
			const currency = row.original.companyCurrency?.currency;
			return amount ? `${currency?.code || ""} ${moneyFormat(amount)}` : "--";
		},
	},
	{
		id: "periodStartDate",
		accessorKey: "periodStartDate",
		header: "Period Start Date",
		size: 200,
		cell: ({ row }) => formatDate(row.original.periodStartDate),
	},
	{
		id: "periodEndDate",
		accessorKey: "periodEndDate",
		header: "Period End Date",
		size: 200,
		cell: ({ row }) => formatDate(row.original.periodEndDate),
	},
	{
		id: "prorate",
		accessorKey: "prorate",
		header: "Prorate",
		size: 140,
		cell: ({ row }) => (row.original.prorate ? "Yes" : "No"),
	},
	{
		id: "paymentFrequency",
		accessorKey: "paymentFrequency",
		header: "Payment Frequency",
		size: 200,
		cell: ({ row }) =>
			row.original.paymentFrequency
				? enumToStringConverter(row.original.paymentFrequency)
				: "--",
	},
];

export const bonusColumnVisibility: Record<string, boolean> = {
	name: true,
	code: true,
	description: true,
	bonusGroup: true,
	fixedComponent: true,
	rateComponent: true,
	employees: true,
	payPeriods: false,
	active: true,
	minValue: false,
	maxValue: false,
	periodStartDate: true,
	periodEndDate: true,
	prorate: false,
	paymentFrequency: true,
};
